import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle, 
  Store, UtensilsCrossed, AlertCircle, RefreshCw,
  ChevronRight, ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';

export default function SetupWizard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const restaurantFileRef = useRef(null);
  const menuFileRef = useRef(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await api.get('/admin/setup/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        toast.error('Failed to load setup data');
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, [api]);

  const downloadTemplate = async (type) => {
    try {
      const response = await api.get('/admin/import/template/' + type);
      const data = response.data;
      
      const header = data.columns.join(',');
      const values = data.columns.map(function(col) {
        const val = data.example_row[col] || '';
        if (val.includes(',')) return '"' + val + '"';
        return val;
      }).join(',');
      
      const csvContent = header + '\n' + values;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = type + '_template.csv';
      link.click();
      
      toast.success(type + ' template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async (file, type) => {
    if (!file) return;
    
    setImporting(true);
    setImportResults(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'restaurants' ? '/admin/import/restaurants' : '/admin/import/menu-items';
      const response = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResults(response.data);
      
      if (response.data.success > 0) {
        toast.success('Imported ' + response.data.success + ' items');
      }
      
      if (response.data.failed > 0) {
        toast.warning(response.data.failed + ' rows failed to import');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResults({ success: 0, failed: 1, errors: [error.response?.data?.detail || 'Unknown error'] });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Setup Wizard</h1>
        <p className="text-slate-600 mt-1">Quickly onboard restaurants and menu items in bulk</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 bg-white rounded-lg p-4 shadow-sm overflow-x-auto">
        <button
          onClick={() => setActiveStep(0)}
          className={'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ' + (activeStep === 0 ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600')}
        >
          <Store className="w-5 h-5" />
          <span className="hidden sm:inline">Overview</span>
        </button>
        <ChevronRight className="w-5 h-5 text-slate-400" />
        <button
          onClick={() => setActiveStep(1)}
          className={'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ' + (activeStep === 1 ? 'bg-orange-500 text-white' : activeStep > 1 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600')}
        >
          <FileSpreadsheet className="w-5 h-5" />
          <span className="hidden sm:inline">Restaurants</span>
        </button>
        <ChevronRight className="w-5 h-5 text-slate-400" />
        <button
          onClick={() => setActiveStep(2)}
          className={'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ' + (activeStep === 2 ? 'bg-orange-500 text-white' : activeStep > 2 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600')}
        >
          <UtensilsCrossed className="w-5 h-5" />
          <span className="hidden sm:inline">Menu Items</span>
        </button>
        <ChevronRight className="w-5 h-5 text-slate-400" />
        <button
          onClick={() => setActiveStep(3)}
          className={'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ' + (activeStep === 3 ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600')}
        >
          <CheckCircle className="w-5 h-5" />
          <span className="hidden sm:inline">Complete</span>
        </button>
      </div>

      {/* Step 0: Overview */}
      {activeStep === 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-orange-500" />
                Current Database Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{stats?.restaurants || 0}</p>
                  <p className="text-sm text-slate-500">Restaurants</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{stats?.menu_items || 0}</p>
                  <p className="text-sm text-slate-500">Menu Items</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{stats?.users || 0}</p>
                  <p className="text-sm text-slate-500">Users</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{stats?.orders || 0}</p>
                  <p className="text-sm text-slate-500">Orders</p>
                </div>
              </div>
              
              {stats?.is_fresh_install && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Fresh Installation</p>
                    <p className="text-sm text-blue-600">Your database is empty. Use this wizard to add data.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Setup Guide</CardTitle>
              <CardDescription>Follow these steps to bulk import your data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-medium">1</div>
                  <div>
                    <p className="font-medium text-slate-900">Download CSV Template</p>
                    <p className="text-sm text-slate-500">Get the template with all required columns</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-medium">2</div>
                  <div>
                    <p className="font-medium text-slate-900">Fill in Your Data</p>
                    <p className="text-sm text-slate-500">Add your restaurants/menu items</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-medium">3</div>
                  <div>
                    <p className="font-medium text-slate-900">Upload & Import</p>
                    <p className="text-sm text-slate-500">Upload the CSV file</p>
                  </div>
                </div>
              </div>
              
              <Button onClick={() => setActiveStep(1)} className="w-full btn-primary mt-4" data-testid="start-setup-btn">
                Start Import
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 1: Import Restaurants */}
      {activeStep === 1 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-orange-500" />
                Import Restaurants
              </CardTitle>
              <CardDescription>Upload a CSV file with restaurant data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Download Template</p>
                  <p className="text-sm text-slate-500">Get the CSV template</p>
                </div>
                <Button variant="outline" onClick={() => downloadTemplate('restaurants')} data-testid="download-restaurants-template">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              <div
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-orange-400 cursor-pointer"
                onClick={() => restaurantFileRef.current?.click()}
              >
                <input
                  ref={restaurantFileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleImport(e.target.files[0], 'restaurants')}
                />
                {importing ? (
                  <RefreshCw className="w-10 h-10 text-orange-500 animate-spin mx-auto" />
                ) : (
                  <Upload className="w-10 h-10 text-slate-400 mx-auto" />
                )}
                <p className="mt-3 font-medium text-slate-700">Click to upload CSV</p>
              </div>

              {importResults && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">{importResults.success} imported</span>
                    </div>
                    {importResults.failed > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-red-100 rounded-lg">
                        <XCircle className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-700">{importResults.failed} failed</span>
                      </div>
                    )}
                  </div>
                  {importResults.errors && importResults.errors.length > 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="font-medium text-red-800 mb-2">Errors:</p>
                      <ul className="text-sm text-red-600 space-y-1">
                        {importResults.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setActiveStep(0)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button onClick={() => { setImportResults(null); setActiveStep(2); }} className="btn-primary">
              Next: Menu Items <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Import Menu Items */}
      {activeStep === 2 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UtensilsCrossed className="w-5 h-5 text-orange-500" />
                Import Menu Items
              </CardTitle>
              <CardDescription>Upload menu items. Restaurants must exist first.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Download Template</p>
                  <p className="text-sm text-slate-500">Get the CSV template</p>
                </div>
                <Button variant="outline" onClick={() => downloadTemplate('menu_items')} data-testid="download-menu-template">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              <div
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-orange-400 cursor-pointer"
                onClick={() => menuFileRef.current?.click()}
              >
                <input
                  ref={menuFileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleImport(e.target.files[0], 'menu_items')}
                />
                {importing ? (
                  <RefreshCw className="w-10 h-10 text-orange-500 animate-spin mx-auto" />
                ) : (
                  <Upload className="w-10 h-10 text-slate-400 mx-auto" />
                )}
                <p className="mt-3 font-medium text-slate-700">Click to upload CSV</p>
              </div>

              {importResults && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">{importResults.success} imported</span>
                    </div>
                    {importResults.failed > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-red-100 rounded-lg">
                        <XCircle className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-700">{importResults.failed} failed</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => { setImportResults(null); setActiveStep(1); }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button onClick={() => { setImportResults(null); setActiveStep(3); }} className="btn-primary">
              Complete <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Complete */}
      {activeStep === 3 && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Setup Complete!</h2>
            <p className="text-slate-600 mb-6">Your data has been imported successfully.</p>
            
            <div className="flex justify-center gap-4 mb-8">
              <div className="p-4 bg-slate-50 rounded-lg text-center min-w-[100px]">
                <p className="text-2xl font-bold text-slate-900">{stats?.restaurants || 0}</p>
                <p className="text-sm text-slate-500">Restaurants</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center min-w-[100px]">
                <p className="text-2xl font-bold text-slate-900">{stats?.menu_items || 0}</p>
                <p className="text-sm text-slate-500">Menu Items</p>
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => { setImportResults(null); setActiveStep(0); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Import More
              </Button>
              <Button className="btn-primary" onClick={() => window.location.href = '/admin/restaurants'}>
                <Store className="w-4 h-4 mr-2" />
                View Restaurants
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
